// SPDX-License-Identifier: AGPL-3.0-or-later

import {useShouldAnimate} from '@app/features/app/hooks/useShouldAnimate';
import styles from '@app/features/cosmetics/components/ProfileEffectOverlay.module.css';
import UltraLayouts from '@app/features/cosmetics/state/UltraLayouts';
import {getCosmeticAssetURL} from '@app/features/cosmetics/utils/CosmeticAssetUtils';
import {loadProfilePart} from '@app/features/cosmetics/utils/UltraFrontAssets';
import {useAutoplayExpandedProfileAnimations} from '@app/features/user/hooks/useAutoplayExpandedProfileAnimations';
import type {MediaProxyImageSize} from '@fluxer/constants/src/MediaProxyImageSizes';
import type {
	CosmeticAssetDataResponse,
	EffectLayers,
} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

// Effects cover a whole profile card; one open profile at a time asks for the full upload size.
const PROFILE_EFFECT_CSS_SIZE = 480;
// High-resolution layers are uploaded up to 1024 px per side and served whole.
const PROFILE_EFFECT_HD_SIZE: MediaProxyImageSize = 1024;

interface ProfileEffectOverlayProps {
	effect: CosmeticAssetDataResponse | null | undefined;
	// An open profile plays the high-resolution layers when the effect has them; previews keep the main image.
	hd?: boolean;
}

export function ProfileEffectOverlay({effect, hd = false}: ProfileEffectOverlayProps) {
	if (effect == null) return null;
	return (
		<ProfileEffectImage effect={effect} hd={hd} data-flx="cosmetics.profile-effect-overlay.profile-effect-image" />
	);
}

const ProfileEffectImage = observer(function ProfileEffectImage({
	effect,
	hd,
}: {
	effect: CosmeticAssetDataResponse;
	hd: boolean;
}) {
	const autoplay = useAutoplayExpandedProfileAnimations();
	const shouldAnimate = useShouldAnimate({kind: 'banner', isHovering: autoplay});
	const [failedUrl, setFailedUrl] = useState<string | null>(null);
	const [failedLayers, setFailedLayers] = useState<string | null>(null);
	const layers =
		hd && shouldAnimate && failedLayers !== effect.item_id ? UltraLayouts.effectLayersFor(effect.item_id) : null;
	if (layers === undefined) return null;
	if (layers) {
		return (
			<ProfileEffectLayers
				key={`${effect.item_id}:${layers.intro}:${layers.loop}`}
				itemId={effect.item_id}
				layers={layers}
				onFailed={setFailedLayers}
				data-flx="cosmetics.profile-effect-overlay.profile-effect-layers"
			/>
		);
	}
	const url = getCosmeticAssetURL('profile_effect', effect, PROFILE_EFFECT_CSS_SIZE, shouldAnimate);
	if (!url || url === failedUrl) return null;
	return (
		<img
			className={styles.image}
			src={url}
			alt=""
			aria-hidden
			draggable={false}
			onError={() => setFailedUrl(url)}
			data-flx="cosmetics.profile-effect-overlay.image"
		/>
	);
});

interface ProfileEffectLayersProps {
	itemId: string;
	layers: EffectLayers;
	onFailed: (itemId: string) => void;
}

interface LayerUrls {
	intro: string;
	loop: string;
}

// The intro plays once, then the loop takes over. Both are downloaded before the intro shows, and each open plays
// them from new blob URLs: an animated image shares its timeline with every other image on the same URL, so a new
// URL starts the intro from its first frame without the network. A failed layer plays the main image.
function ProfileEffectLayers({itemId, layers, onFailed}: ProfileEffectLayersProps) {
	const urlOf = (hash: string) =>
		getCosmeticAssetURL(
			'profile_effect',
			{item_id: itemId, asset: hash},
			PROFILE_EFFECT_HD_SIZE,
			true,
			PROFILE_EFFECT_HD_SIZE,
		);
	const introSource = urlOf(layers.intro);
	const loopSource = urlOf(layers.loop);
	const [urls, setUrls] = useState<LayerUrls | null>(null);
	const [introLoaded, setIntroLoaded] = useState(false);
	const [introDone, setIntroDone] = useState(false);
	const [loopShown, setLoopShown] = useState(false);

	useEffect(() => {
		let live = true;
		let made: LayerUrls | null = null;
		Promise.all([loadProfilePart(introSource), loadProfilePart(loopSource)]).then(
			([intro, loop]) => {
				if (!live) return;
				made = {intro: URL.createObjectURL(intro), loop: URL.createObjectURL(loop)};
				setUrls(made);
			},
			() => {
				if (live) onFailed(itemId);
			},
		);
		return () => {
			live = false;
			if (!made) return;
			URL.revokeObjectURL(made.intro);
			URL.revokeObjectURL(made.loop);
		};
	}, [introSource, loopSource, itemId, onFailed]);

	// One timer per open profile, from the intro's first frame on screen to the loop.
	useEffect(() => {
		if (!introLoaded || introDone) return;
		const timer = setTimeout(() => setIntroDone(true), layers.intro_ms);
		return () => clearTimeout(timer);
	}, [introLoaded, introDone, layers.intro_ms]);

	if (!urls) return null;
	const failed = () => onFailed(itemId);
	return (
		<>
			{loopShown ? null : (
				<img
					className={styles.image}
					src={urls.intro}
					alt=""
					aria-hidden
					draggable={false}
					onLoad={() => setIntroLoaded(true)}
					onError={failed}
					data-flx="cosmetics.profile-effect-overlay.intro"
				/>
			)}
			{introDone ? (
				<img
					className={styles.image}
					src={urls.loop}
					alt=""
					aria-hidden
					draggable={false}
					onLoad={() => setLoopShown(true)}
					onError={failed}
					data-flx="cosmetics.profile-effect-overlay.loop"
				/>
			) : null}
		</>
	);
}
